import {
  AlertCircle,
  Check,
  CheckCheck,
  ClipboardList,
  CloudCheck,
  Eye,
  Loader2,
  MoreHorizontal,
  RotateCw,
  Send,
  Sparkles
} from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";

import type {
  OutgoingIntakeFile,
  OutgoingIntakeSubmission
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { FilterChip } from "@/components/ui/filter-chip";
import {
  ResizablePanel,
  ResizablePanelGroup,
  ResizableHandle
} from "@/components/ui/resizable";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { PageBar } from "@/features/intakeBuilder";
import { PublicOutgoingIntake } from "@/pages/PublicOutgoingIntake/PublicOutgoingIntake";
import { cn } from "@/lib/utils";

import { useRequestsIde } from "../context/RequestsIdeContext";
import { usePreviewInitialData } from "../editor/previewData";
import { FinishSheet } from "../editor/review/FinishSheet";
import { openIntakeFile } from "../editor/review/openIntakeFile";
import { ReviewTable } from "../editor/review/ReviewTable";
import { RevisionDialog } from "../editor/review/RevisionDialog";
import {
  REVIEW_FILTERS,
  matchesFilter,
  type ReviewFilter
} from "../editor/reviewModel";
import { TimelinePopover } from "../editor/TimelinePopover";
import {
  useSubmissionReview,
  type ReviewRow,
  type SaveState
} from "../editor/useSubmissionReview";
import { ASSISTANT_NAME, clockLabel } from "../model";

type Props = {
  intakeId: string;
  submissionId: string;
  clientName: string | null;
};

const SaveIndicator: FC<{ state: SaveState; onRetry: () => void }> = ({
  state,
  onRetry
}) => {
  if (state === "idle") return null;
  if (state === "error")
    return (
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex shrink-0 items-center gap-1 text-sm whitespace-nowrap text-danger hover:underline"
      >
        <AlertCircle className="size-3.5" /> Not Saved · Retry
      </button>
    );
  return (
    <span
      role="status"
      className="inline-flex shrink-0 items-center gap-1 text-sm whitespace-nowrap text-fg-3"
    >
      {state === "saving" ? (
        <>
          <Loader2 className="size-3.5 animate-spin" /> Saving…
        </>
      ) : (
        <>
          <CloudCheck className="size-3.5" /> Saved
        </>
      )}
    </span>
  );
};

const Dot: FC<{ className: string }> = ({ className }) => (
  <span className={cn("size-1.5 shrink-0 rounded-full", className)} />
);

const reviewStatus = (
  s: OutgoingIntakeSubmission
): { tone: StatusTone; label: string } => {
  if (s.review_status === "revision_requested")
    return { tone: "warn", label: "Changes Requested" };
  if (s.review_status === "accepted")
    return { tone: "success", label: "Accepted" };
  return { tone: "info", label: "Ready to Review" };
};

/**
 * A submission under review: a dense checklist of every answer with inline
 * Accept / Flag, what changed since the last submission, the AI check's upload
 * warnings and where each answer saves. The client's own view is a toggle;
 * finishing is one dialog (send back) or one sheet (accept and file).
 */
export const ReviewPage: FC<Props> = ({
  intakeId,
  submissionId,
  clientName
}) => {
  const { clientId, openList, setSelectedAnswerKey, requestPrompt } =
    useRequestsIde();
  const review = useSubmissionReview(intakeId, submissionId, clientName);
  const { intake, submission, rows, counts, locked } = review;
  const [filter, setFilter] = useState<ReviewFilter>("all");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [clientView, setClientView] = useState(false);
  const [dialog, setDialog] = useState<null | "revision" | "finish">(null);

  const visible = useMemo(
    () => rows.filter((r) => matchesFilter(r, filter)),
    [filter, rows]
  );
  const selected = rows.find((r) => r.key === selectedKey) ?? null;

  const select = useCallback(
    (row: ReviewRow) => {
      setSelectedKey(row.key);
      setSelectedAnswerKey(row.key);
    },
    [setSelectedAnswerKey]
  );
  const openFile = useCallback(
    (f: OutgoingIntakeFile) => void openIntakeFile(clientId, intakeId, f),
    [clientId, intakeId]
  );
  const initialData = usePreviewInitialData(intake, review.schema, submission);

  if (!intake || !submission || !initialData) {
    return (
      <div className="flex h-full items-center justify-center text-base text-fg-3">
        {review.loading ? (
          <Spinner className="size-4" />
        ) : (
          "This submission no longer exists."
        )}
      </div>
    );
  }

  const prevNumber = review.submissionNumber - 1;
  const hasPrevious = Boolean(review.previous);
  const submittedAt = clockLabel(
    submission.submitted_at ?? submission.created_at
  );
  const status = reviewStatus(submission);
  const primary = review.superseded
    ? null
    : counts.rejected > 0 && !locked
    ? {
        label: `Request Changes (${counts.rejected})…`,
        icon: Send,
        onClick: () => setDialog("revision")
      }
    : !locked
    ? {
        label: "Accept and File…",
        icon: Check,
        onClick: () => setDialog("finish")
      }
    : review.accepted
    ? {
        label: "File…",
        icon: Check,
        onClick: () => setDialog("finish"),
        secondary: true
      }
    : null;

  const table = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-11 shrink-0 items-center gap-1.5 overflow-hidden border-b border-line-1 px-4 whitespace-nowrap">
        {REVIEW_FILTERS.filter((f) => f.id !== "changed" || hasPrevious).map(
          (f) => (
            <FilterChip
              key={f.id}
              active={filter === f.id}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              <span className="text-fg-3 tabular-nums">
                {review.filterCounts[f.id]}
              </span>
            </FilterChip>
          )
        )}
        <div className="flex-1" />
        <span className="flex min-w-0 items-center gap-3 truncate text-sm text-fg-3">
          {hasPrevious ? (
            <span className="inline-flex items-center gap-1.5">
              <Dot className="bg-info" />
              {counts.changed} changed since #{prevNumber}
            </span>
          ) : null}
          {counts.attention ? (
            <span className="inline-flex items-center gap-1.5">
              <Dot className="bg-danger" />
              {counts.attention}{" "}
              {counts.attention === 1 ? "upload needs" : "uploads need"}{" "}
              attention
            </span>
          ) : null}
          <span>
            {counts.accepted} accepted · {counts.rejected} flagged ·{" "}
            {counts.pending} pending
          </span>
        </span>
        {!locked && counts.pending ? (
          <Button
            variant="secondary"
            size="xs"
            disabled={review.safeKeys.length === 0}
            onClick={() => review.acceptKeys(review.safeKeys)}
          >
            <CheckCheck />
            {review.safeKeys.length
              ? `Accept ${review.safeKeys.length} Without Warnings`
              : "Nothing Safe to Accept"}
          </Button>
        ) : null}
      </div>

      <ReviewTable
        rows={visible}
        selectedKey={selectedKey}
        emptyLabel={
          rows.length
            ? "Nothing matches this filter."
            : "The client didn't answer anything."
        }
        locked={locked}
        clientFirstName={review.clientFirstName}
        onSelect={select}
        onDecision={review.setDecision}
        onNote={review.setNote}
        onOpenFile={openFile}
      />
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar
        crumbs={[
          { label: "Intake Requests", onClick: () => openList() },
          { label: intake.title || "Request", onClick: review.openIntake }
        ]}
        title={`Submission #${review.submissionNumber}`}
        meta={
          <>
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            {submittedAt ? (
              <span className="text-sm text-fg-3">Submitted {submittedAt}</span>
            ) : null}
          </>
        }
        actions={
          <>
            {!locked ? (
              <SaveIndicator
                state={review.saveState}
                onRetry={review.retrySave}
              />
            ) : null}
            <TimelinePopover
              intake={intake}
              submissions={review.submissions}
              clientId={clientId}
            />
            <Button
              variant={clientView ? "tonal" : "ghost"}
              size="sm"
              aria-pressed={clientView}
              onClick={() => setClientView((v) => !v)}
            >
              <Eye /> View as Client
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="More">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onSelect={review.openIntake}>
                  <ClipboardList /> Open Request
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    requestPrompt(
                      "Assess this submission against the record and tell me what to accept and what to flag.",
                      { send: true }
                    )
                  }
                >
                  <Sparkles /> Ask {ASSISTANT_NAME} to Assess
                </DropdownMenuItem>
                {!locked && counts.rejected > 0 ? (
                  <DropdownMenuItem onSelect={() => setDialog("finish")}>
                    <Check /> Accept and File…
                  </DropdownMenuItem>
                ) : null}
                {review.submissions.length > 1 ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-sm font-normal text-fg-3">
                      Submissions
                    </DropdownMenuLabel>
                    {review.submissions.map((s, i) => (
                      <DropdownMenuItem
                        key={s.outgoing_intake_submission_id}
                        disabled={
                          s.outgoing_intake_submission_id === submissionId
                        }
                        onSelect={() => review.openSubmission(s)}
                      >
                        Submission #{i + 1}
                        <span className="ml-auto text-sm text-fg-3">
                          {clockLabel(s.submitted_at ?? s.created_at)}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            {primary ? (
              <Button
                size="sm"
                variant={primary.secondary ? "secondary" : "primary"}
                onClick={primary.onClick}
              >
                <primary.icon /> {primary.label}
              </Button>
            ) : null}
          </>
        }
      />

      {review.superseded && review.latest ? (
        <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line-1 bg-warn-soft px-4 text-base text-warn">
          <RotateCw className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            A newer submission came in. This one is read-only.
          </span>
          <Button
            variant="secondary"
            size="xs"
            onClick={() =>
              review.latest && review.openSubmission(review.latest)
            }
          >
            Open Submission #{review.submissions.length}
          </Button>
        </div>
      ) : null}

      {clientView ? (
        <ResizablePanelGroup
          orientation="horizontal"
          className="min-h-0 flex-1"
        >
          <ResizablePanel
            id={`review-table-${submissionId}`}
            defaultSize="55"
            minSize="35"
          >
            {table}
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel
            id={`review-client-${submissionId}`}
            defaultSize="45"
            minSize="30"
          >
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex h-11 shrink-0 items-center border-b border-line-1 px-4 text-base font-medium text-fg">
                <span className="truncate">
                  {selected ? `Client View · ${selected.label}` : "Client View"}
                </span>
              </div>
              <div className="min-h-0 flex-1 bg-fill-1">
                <PublicOutgoingIntake
                  mode="preview"
                  hideTopBar
                  embeddedPreview
                  previewViewport="responsive"
                  tokenOverride="__review__"
                  focusSingleNode
                  activeNodeId={
                    selected?.stepNodeId ??
                    review.steps[0]?.node.node_id ??
                    null
                  }
                  activeInstanceId={selected?.instanceId ?? null}
                  activeQuestionId={
                    selected?.instanceId ? selected.node.node_id : null
                  }
                  reviewMode
                  reviewDecisionByKey={review.reviewDecisionByKey}
                  reviewNoteByKey={review.reviewNoteByKey}
                  reviewCommentsByKey={review.reviewCommentsByKey}
                  onReviewDecision={locked ? undefined : review.setDecision}
                  onReviewNoteChange={locked ? undefined : review.setNote}
                  onActiveNodeChange={(nodeId) => {
                    if (!nodeId || nodeId === selected?.stepNodeId) return;
                    const first = rows.find((r) => r.stepNodeId === nodeId);
                    if (first) setSelectedKey(first.key);
                  }}
                  initialData={initialData}
                />
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="min-h-0 flex-1">{table}</div>
      )}

      <RevisionDialog
        open={dialog === "revision"}
        onOpenChange={(o) => setDialog(o ? "revision" : null)}
        review={review}
      />
      <FinishSheet
        open={dialog === "finish"}
        onOpenChange={(o) => setDialog(o ? "finish" : null)}
        review={review}
        intakeId={intakeId}
        submissionId={submissionId}
      />
    </div>
  );
};
